/**
 * React Query hook for Vertex AI index status.
 * Polls every 30s when pending documents exist, stops when all indexed.
 * Auto-triggers reindexing on first detection of pending documents (workspace admins only: the
 * server refuses anyone else).
 */

import { useRef, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getWorkspaceVertexIndexStatus,
  triggerWorkspaceReindex,
  type VertexIndexStatusResponse,
} from "../services/naraDoor";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useIsWorkspaceAdmin } from "./useWorkspaceMembers";

// The status route is member scope (viewer and up): an uploader, or a caller with no seat, is refused it.
const INDEX_STATUS_ROLES = ["owner", "admin", "editor", "viewer"];
export const canSeeIndexStatus = (role: string | null) => role !== null && INDEX_STATUS_ROLES.includes(role);

/** `enabled` false (a role that may not see it): no query, no reindex. */
export function useVertexIndexStatus(enabled: boolean) {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.id;
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const [reindexFailed, setReindexFailed] = useState(false);
  // The workspace already reindexed by this mount: a refresh after the reindex changes the data and must not trigger it again.
  const reindexedWorkspaceId = useRef<string>();

  const query = useQuery<VertexIndexStatusResponse>({
    queryKey: ["vertexIndexStatus", workspaceId],
    queryFn: () => getWorkspaceVertexIndexStatus(workspaceId!),
    enabled: enabled && !!workspaceId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 30s if there are pending docs, otherwise stop
      return data && data.total_pending > 0 ? 30000 : false;
    },
  });

  // Auto-trigger reindex once per workspace when pending documents are detected
  useEffect(() => {
    if (
      query.data &&
      query.data.total_pending > 0 &&
      isWorkspaceAdmin &&
      workspaceId &&
      reindexedWorkspaceId.current !== workspaceId
    ) {
      reindexedWorkspaceId.current = workspaceId;
      setReindexFailed(false);
      triggerWorkspaceReindex(workspaceId).catch((err) => {
        console.error("Auto-reindex failed", { workspaceId, message: err instanceof Error ? err.message : String(err) });
        setReindexFailed(true);
      });
    }
  }, [query.data, workspaceId, isWorkspaceAdmin]);

  return { ...query, reindexFailed };
}
